<template>
  <el-dialog
    :title="title"
    :visible="visible"
    :width="width"
    :append-to-body="true"
    :destroy-on-close="destroyOnClose"
    @close="handleClose"
    class="edit-dialog"
    ref="editDialogRef"
    @open="handleDialogOpen"
  >
    <!-- 自定义标题栏 -->
    <template #title>
      <div class="custom-title">
        <icon-title
          :icon-color="'var(--text-color)'"
          :title="title"
          icon="icon-shuxian"
          :img-src="imgSrc"
        />
      </div>
    </template>
    <!-- 表单内容区域 -->
    <el-form
      ref="editForm"
      :model="formData"
      :rules="mergeRules"
      :label-width="labelWidth"
      class="form-container"
    >
      <!-- 两列布局：通过计算属性拆分字段为左右两列 -->
      <div class="form-row">
        <!-- 左列 -->
        <div class="form-col">
          <template v-for="(field, index) in leftFields">
            <el-form-item
              :label="field.label"
              :prop="field.prop"
              :required="field.required"
              :class="{ 'form-item': true, top: labelPosition === 'top' }"
            >
              <!-- 根据字段类型渲染不同组件 -->
              <template v-if="field.type === 'input'">
                <!--@input="(val) => handleInputFormat(val, field)"  实时过滤非法字符-->
                <el-input
                  v-model="formData[field.prop]"
                  :placeholder="field.placeholder || `请输入${field.label}`"
                  :maxlength="field.maxlength"
                  :type="field.inputType || 'text'"
                  @input="(val) => handleInputFormat(val, field)"
                ></el-input>
              </template>

              <template v-if="field.type === 'select'">
                <el-select
                  v-model="formData[field.prop]"
                  :placeholder="field.placeholder || `请选择${field.label}`"
                  clearable
                >
                  <el-option
                    v-for="option in field.options"
                    :key="option.value"
                    :label="option.label"
                    :value="option.value"
                  ></el-option>
                </el-select>
              </template>

              <template v-if="field.type === 'textarea'">
                <el-input
                  v-model="formData[field.prop]"
                  :placeholder="field.placeholder || `请输入${field.label}`"
                  type="textarea"
                  :rows="field.rows || 4"
                  :maxlength="field.maxlength || 200"
                  show-word-limit
                ></el-input>
              </template>
              <!-- 日期选择器 -->
              <template v-if="field.type === 'date'">
                <el-date-picker
                  v-model="formData[field.prop]"
                  :type="field.dateType || 'date'"
                  :placeholder="field.placeholder || `请选择${field.label}`"
                  :format="field.format || 'yyyy-MM-dd'"
                  :value-format="field.valueFormat || 'yyyy-MM-dd'"
                  :disabled-date="field.disabledDate"
                  :shortcuts="field.shortcuts"
                  clearable
                  style="width: 100%"
                ></el-date-picker>
              </template>
              <!-- 图片上传组件 -->
              <template v-if="field.type === 'upload'">
                <UploadImg
                  :file-list="getUploadFileList(field.prop)"
                  :limit="3"
                  :prop="field.prop"
                  accept="image/jpg,image/png,image/jpeg"
                  :file-size="5"
                  tip-text="支持JPG/PNG/JPEG格式，单文件不超过5M"
                  @upload-success="handleUploadSuccess"
                />
              </template>
            </el-form-item>
          </template>
        </div>

        <!-- 右列 -->
        <div class="form-col">
          <template v-for="(field, index) in rightFields">
            <el-form-item
              :label="field.label"
              :prop="field.prop"
              :required="field.required"
              :class="{ 'form-item': true, top: labelPosition === 'top' }"
            >
              <!-- 同左列，根据类型渲染组件 -->
              <template v-if="field.type === 'input'">
                <el-input
                  v-model="formData[field.prop]"
                  :placeholder="field.placeholder || `请输入${field.label}`"
                  :maxlength="field.maxlength"
                  :type="field.inputType || 'text'"
                  @input="(val) => handleInputFormat(val, field)"
                ></el-input>
              </template>

              <template v-if="field.type === 'select'">
                <el-select
                  v-model="formData[field.prop]"
                  :placeholder="field.placeholder || `请选择${field.label}`"
                  clearable
                >
                  <el-option
                    v-for="option in field.options"
                    :key="option.value"
                    :label="option.label"
                    :value="option.value"
                  ></el-option>
                </el-select>
              </template>

              <template v-if="field.type === 'textarea'">
                <el-input
                  v-model="formData[field.prop]"
                  type="textarea"
                  :rows="field.rows || 4"
                  :maxlength="field.maxlength || 200"
                  show-word-limit
                ></el-input>
              </template>
              <!-- 日期选择器 -->
              <template v-if="field.type === 'date'">
                <el-date-picker
                  v-model="formData[field.prop]"
                  :type="field.dateType || 'date'"
                  :placeholder="field.placeholder || `请选择${field.label}`"
                  :format="field.format || 'yyyy-MM-dd'"
                  :value-format="field.valueFormat || 'yyyy-MM-dd'"
                  :disabled-date="field.disabledDate"
                  :shortcuts="field.shortcuts"
                  clearable
                  style="width: 100%"
                ></el-date-picker>
              </template>
              <!-- 图片上传组件 -->
              <template v-if="field.type === 'upload'">
                <UploadImg
                  :file-list="getUploadFileList(field.prop)"
                  :limit="3"
                  :prop="field.prop"
                  accept="image/jpg,image/png,image/jpeg"
                  :file-size="5"
                  tip-text="支持JPG/PNG/JPEG格式，单文件不超过5M"
                  @upload-success="handleUploadSuccess"
                />
              </template>
            </el-form-item>
          </template>
        </div>
      </div>

      <!-- 单独占一行的字段（如备注） -->
      <div v-if="fullWidthFields.length" class="full-width-fields">
        <template v-for="(field, index) in fullWidthFields">
          <el-form-item
            style="margin-bottom: 20px"
            :label="field.label"
            :prop="field.prop"
            :required="field.required"
            :class="{ 'form-item': true, top: labelPosition === 'top' }"
          >
            <!-- 输入框 -->
            <el-input
              v-if="field.type === 'input'"
              v-model="formData[field.prop]"
              :placeholder="field.placeholder || `请输入${field.label}`"
              :type="field.inputType || 'text'"
              :rows="field.rows || 4"
              :maxlength="field.maxlength || 200"
              show-word-limit
              @input="(val) => handleInputFormat(val, field)"
            ></el-input>
            <!-- textarea -->
            <el-input
              v-if="field.type === 'textarea'"
              v-model="formData[field.prop]"
              :placeholder="field.placeholder || `请输入${field.label}`"
              type="textarea"
              :rows="field.rows || 4"
              :maxlength="field.maxlength || 200"
              show-word-limit
              @input="(val) => handleInputFormat(val, field)"
            ></el-input>

            <template v-if="field.type === 'select'">
              <el-select
                style="width: 100%"
                v-model="formData[field.prop]"
                :placeholder="field.placeholder || `请选择${field.label}`"
                clearable
              >
                <el-option
                  v-for="option in field.options"
                  :key="option.value"
                  :label="option.label"
                  :value="option.value"
                ></el-option>
              </el-select>
            </template>
            <!-- 日期选择器 -->
            <template v-if="field.type === 'date'">
              <el-date-picker
                v-model="formData[field.prop]"
                :type="field.dateType || 'date'"
                :placeholder="field.placeholder || `请选择${field.label}`"
                :format="field.format || 'yyyy-MM-dd'"
                :value-format="field.valueFormat || 'yyyy-MM-dd'"
                :disabled-date="field.disabledDate"
                :shortcuts="field.shortcuts"
                clearable
                style="width: 100%"
              ></el-date-picker>
            </template>
            <!-- 图片上传组件 -->
            <template v-if="field.type === 'upload'">
              <UploadImg
                :file-list="getUploadFileList(field.prop)"
                :limit="3"
                :prop="field.prop"
                accept="image/jpg,image/png,image/jpeg"
                :file-size="5"
                tip-text="支持JPG/PNG/JPEG格式，单文件不超过5M"
                @upload-success="handleUploadSuccess"
              />
            </template>
          </el-form-item>
        </template>
      </div>
    </el-form>

    <!-- 底部按钮 -->
    <template #footer>
      <div class="dialog-footer">
        <CustomFormButton
          v-for="button in actionButtons.topArticleButtons"
          :isShowList="isShowList"
          :key="button.id"
          :text="button.text"
          :type="button.type"
          :size="button.size"
          :isPlain="button.isPlain"
          :icon="button.icon"
          :customStyle="button.customStyle"
          @click="handleClick(button)"
        />
        <!-- <el-button @click="handleCancel">取消</el-button>
        <el-button type="primary" @click="handleConfirm">确定</el-button> -->
      </div>
    </template>
  </el-dialog>
</template>

<script>
import { actionButtons } from "@/utils/defaultData";
export default {
  name: "EditDialog",
  props: {
    // 控制弹窗显示/隐藏
    visible: {
      type: Boolean,
      required: true,
      default: false,
    },
    // 是否显示操作按钮
    isShowList: {
      type: Array,
      default: () => [],
    },
    // 弹窗标题（如“新增被考核对象”）
    title: {
      type: String,
      default: "编辑信息",
    },
    // 自定义标题栏图标
    imgSrc: {
      type: String,
      default: "",
    },
    // 弹窗宽度
    width: {
      type: [String, Number],
      default: "50%",
    },
    // 表单字段配置（核心配置项）
    fields: {
      type: Array,
      required: true,
      default: () => [],
      /* 字段格式示例：
        {
          label: '食材名称', // 标签文本
          prop: 'name', // 绑定的字段名
          type: 'input', // 组件类型：input/select/textarea
          required: true, // 是否必填
          placeholder: '请输入食材名称', // 占位提示
          maxlength: 50, // 最大长度（仅input/textarea）
          inputType: 'text', // 输入框类型（text/number等）
          options: [{ label: '选项1', value: 1 }] // 下拉选项（仅select）
          fullWidth: false // 是否单独占一行（默认false，即两列布局）
            regex: /^[a-zA-Z0-9_-]+$/, // 格式校验正则（只能输入字母、数字、下划线、横线）
          regexMsg: '只能输入字母、数字、下划线和横线', // 校验失败提示语
          filterInput: true // 是否实时过滤非法字符（默认false，仅校验不过滤）
           // 日期选择器专属配置（type=date时生效）
          dateType: 'date', // 日期类型：date/datetime/daterange等
          format: 'yyyy-MM-dd', // 显示格式
          valueFormat: 'yyyy-MM-dd', // 绑定值格式
          disabledDate: null, // 禁用日期函数
          shortcuts: null // 快捷选项
        }
      */
    },
    // 表单验证规则（同el-form的rules）
    formRules: {
      type: Object,
      default: () => ({}),
    },
    // 初始表单数据（编辑场景时传入）
    initialData: {
      type: Object,
      default: () => ({}),
    },
    // 标签宽度
    labelWidth: {
      type: String,
      default: "150px",
    },
    labelPosition: {
      type: String,
      default: "",
    },
    // 弹窗圆角大小（支持px、%、rem等单位）
    dialogRadius: {
      type: String,
      default: "15px",
    },
    // 弹窗背景色
    dialogBgColor: {
      type: String,
      default: "#fff",
    },
    // 关闭时是否销毁表单（避免缓存）
    destroyOnClose: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      // 表单数据（基于initialData初始化）
      formData: { ...this.initialData },
      // 图片上传组件的文件列表
      // uploadedFiles: [],
    };
  },
  computed: {
    // 动态计算弹窗样式
    dialogStyle() {
      const style = {
        backgroundColor: this.dialogBgColor,
        borderRadius: this.dialogRadius, // 新增的圆角属性
      };
      // if (this.dialogBgImage) {
      //   style.backgroundImage = `url("${this.dialogBgImage}")`;
      //   style.backgroundSize = "cover";
      //   style.backgroundRepeat = "no-repeat";
      // } else if (this.dialogBgGradient) {
      //   // 如果有渐变背景，设置背景图片为渐变
      //   style.backgroundImage = this.dialogBgGradient;
      //   // 如果有渐变背景，移除背景色设置
      //   delete style.backgroundColor;
      // }
      return style;
    },
    // 拆分字段为左列（两列布局）
    leftFields() {
      return this.fields
        .filter((field) => !field.fullWidth)
        .filter((_, index) => index % 2 === 0);
    },
    // 拆分字段为右列（两列布局）
    rightFields() {
      return this.fields
        .filter((field) => !field.fullWidth)
        .filter((_, index) => index % 2 === 1);
    },
    // 单独占一行的字段（如备注）
    fullWidthFields() {
      return this.fields.filter((field) => field.fullWidth);
    },
    // 合并自定义格式校验规则和父组件传入的rules
    mergeRules() {
      const newRules = { ...this.formRules };
      // 遍历字段，为有regex的字段添加格式校验规则
      this.fields.forEach((field) => {
        if (field.prop && field.regex) {
          // 初始化该字段的规则数组
          if (!newRules[field.prop]) {
            newRules[field.prop] = [];
          }
          // 添加强制校验规则（required）
          if (field.required) {
            newRules[field.prop].push({
              required: true,
              message: `请输入${field.label}`,
              trigger: "blur",
            });
          }
          // 添加格式校验规则
          newRules[field.prop].push({
            pattern: field.regex,
            message: field.regexMsg || `请输入符合格式的${field.label}`,
            trigger: ["blur", "change"],
          });
        } else if (field.prop && field.required && field.type === "date") {
          // 单独处理日期类型的必填校验
          if (!newRules[field.prop]) {
            newRules[field.prop] = [];
          }
          newRules[field.prop].push({
            required: true,
            message: `请选择${field.label}`,
            trigger: "change",
          });
        }
      });
      return newRules;
    },
  },
  watch: {
    // 监听初始数据变化（编辑场景时更新表单）
    initialData: {
      deep: true,
      handler(newVal) {
        this.formData = { ...newVal };
      },
    },
    // 确保每次打开弹窗都重新挂载最新规则
    formRules: {
      deep: true,
      handler() {
        this.$nextTick(() => {
          if (this.$refs.editForm) {
            this.$refs.editForm.clearValidate(); // 清掉旧缓存
          }
        });
      },
    },
    // 新增：监听visible变化，确保弹窗显示时重新应用样式
    // visible(newVal) {
    //   if (newVal) {
    //     this.$nextTick(() => {
    //       this.applyDialogStyle();
    //     });
    //   }
    // },
  },
  mounted() {
    // 初始挂载时应用弹窗背景样式
    // this.$nextTick(() => {
    //   this.applyDialogStyle();
    // });
  },
  methods: {
    // 弹窗打开时触发（ElementUI内置事件）
    handleDialogOpen() {
      this.$nextTick(() => {
        this.applyDialogStyle();
      });
    },
    // 获取指定字段的文件列表（用于回显）
    getUploadFileList(prop) {
      // 此处不需要后续处理了。上传的文件路径已自动补充到formData里
      console.log("获取指定字段的文件列表（用于回显）", prop, this.formData);
      // const value = this.formData[prop];
      // if (!value) return [];
      // // 支持字符串URL或数组格式
      // if (typeof value === "string") {
      //   return [{ name: value.split("/").pop(), url: value }];
      // }
      // if (Array.isArray(value)) {
      //   return value.map((url) => ({
      //     name: url.split("/").pop(),
      //     url: url,
      //   }));
      // }
      // return [];
    },
    // 应用弹窗背景样式
    applyDialogStyle() {
      // 方案1：优先用ref定位（推荐）
      let dialog = this.$refs.editDialogRef?.$el?.querySelector(".el-dialog");
      // 方案2：兼容append-to-body的情况
      if (!dialog && this.appendToBody) {
        dialog = document.querySelector(`.edit-dialog .el-dialog`);
      }
      if (dialog) {
        Object.keys(this.dialogStyle).forEach((key) => {
          dialog.style[key] = this.dialogStyle[key];
        });
      }
    },
    // 实时过滤非法字符（根据regex）
    handleInputFormat(val, field) {
      // 仅当开启filterInput且有regex时生效
      if (!field.filterInput || !field.regex) return;
      // 1. 提取原正则的核心字符类（去掉^和$）
      let regexSource = field.regex.source;
      // 去掉开头的^和结尾的$（避免干扰取反字符类）
      regexSource = regexSource.replace(/^\^/, "").replace(/\$$/, "");
      try {
        // 2. 构造“匹配非法字符”的正则（取反原正则的字符类）
        const invalidRegex = new RegExp(`[^${regexSource}]`, "g");
        // 3. 过滤非法字符
        const validVal = val.replace(invalidRegex, "");
        // 4. 响应式更新
        if (validVal !== this.formData[field.prop]) {
          this.$set(this.formData, field.prop, validVal);
        }
      } catch (e) {
        console.error("过滤正则构造失败：", e);
      }
    },
    // 上传成功回调
    handleUploadSuccess(res, file, fileList, prop) {
      // this.uploadedFiles = fileList.map((item) => ({
      //   url: item.response ? item.response.data : item.url,
      //   name: item.name,
      // }));
      // console.log(
      //   "ImageSelctModal子组件上传成功回调函数",
      //   res,
      //   file,
      //   fileList,
      //   this.uploadedFiles,
      // );
      // 从 fileList 中提取 URL 列表
      const urls = fileList
        .map((item) => {
          // 根据你的接口返回结构调整这里
          return item.response?.data?.url || item.response?.data || item.url;
        })
        .filter(Boolean);

      // 更新到 formData 中（这是关键！）
      this.$set(this.formData, prop, urls.length === 1 ? urls[0] : urls);
      // 同时通知父组件（如果需要）
      this.$emit("upload-success", res, file, fileList, prop);
    },
    // 按钮点击处理方法
    handleClick(button, row) {
      console.log("点击了按钮", button, row);
      // 可以在这里添加按钮点击逻辑
      if (button.handler) {
        this[button.handler](button, row);
      }
    },
    // 关闭弹窗
    handleClose() {
      console.log("弹窗编辑里的取消按钮了", this.visible);
      this.$emit("close");
      if (!this.visible) {
        this.$emit("update:visible", false);
        this.resetForm(); // 重置表单
      }
    },
    // 取消按钮
    handleCancelClick() {
      this.handleClose();
      this.$emit("cancel");
    },
    // 确定按钮（表单验证+提交）
    handleConfirmClick() {
      this.$refs.editForm.validate((valid) => {
        if (valid) {
          console.log("提交表单了", this.formData);
          // 验证通过，将表单数据传递给父组件
          this.$emit("confirm", { ...this.formData });
          if (!this.visible) {
            this.$emit("close");
          }
        }
      });
    },
    // 重置表单
    resetForm() {
      // 重置为初始数据（避免下次打开有残留）
      this.formData = { ...this.initialData };
      // if (this.$refs.editForm) {
      //   this.$refs.editForm.resetFields();
      // }
      this.$nextTick(() => {
        this.$refs.editForm && this.$refs.editForm.clearValidate();
      });
    },
  },
};
</script>

<style scoped lang="scss">
::v-deep.el-dialog__wrapper{
  .el-dialog{
    border-radius: v-bind("dialogRadius");

  }
}
::v-deep.el-form {
  .el-form-item {
    .el-form-item__label {
      line-height: 30px !important;
    }
  }
  .el-form-item.top {
    display: flex;
    flex-direction: column;
    .el-form-item__label {
      text-align: left !important;
    }
    .el-form-item__content {
      margin-left: 0px !important;
    }
  }
}
.edit-dialog {
  ::v-deep .el-dialog__body {
    padding: 20px 30px;
    max-height: 70vh;
    overflow-y: auto;
  }
}

.form-container {
  width: 100%;
}

.form-row {
  display: flex;
  justify-content: space-between;
  width: 100%;
  gap: 20px; /* 两列之间的间距 */
}

.form-col {
  flex: 1; /* 两列平分宽度 */
}

.form-item {
  margin-bottom: 15px; /* 表单项之间的间距 */
}

.full-width-fields {
}

.dialog-footer {
  display: flex;
  justify-content: center;
  gap: 10px;
}
</style>
